import { useEffect, useState } from 'react';
import moment from 'moment-timezone';

/**
 * Generate timezone-aware date ranges
 * @param {string} timezone - IANA timezone identifier (e.g., 'America/New_York', 'UTC')
 * @returns {Object} Date ranges object with moment objects in the specified timezone
 */
export const getTimezoneAwareRanges = (timezone = 'UTC') => {
  const getThisQuarter = timezone === 'UTC'
    ? moment.utc().startOf('quarter')
    : moment.tz(timezone).startOf('quarter');
  const getPreviousQuarter = timezone === 'UTC'
    ? moment.utc().subtract(1, 'Q').startOf('quarter')
    : moment.tz(timezone).subtract(1, 'Q').startOf('quarter');

  return {
    Yesterday: [
      timezone === 'UTC' ? moment.utc().subtract(1, 'days').startOf('day') : moment.tz(timezone).subtract(1, 'days').startOf('day'),
      timezone === 'UTC' ? moment.utc().endOf('day') : moment.tz(timezone).endOf('day'),
    ],
    'Last 7 Days': [
      timezone === 'UTC' ? moment.utc().subtract(7, 'days').startOf('day') : moment.tz(timezone).subtract(7, 'days').startOf('day'),
      timezone === 'UTC' ? moment.utc().endOf('day') : moment.tz(timezone).endOf('day'),
    ],
    'This Month': [
      timezone === 'UTC' ? moment.utc().startOf('month').startOf('day') : moment.tz(timezone).startOf('month').startOf('day'),
      timezone === 'UTC' ? moment.utc().endOf('day') : moment.tz(timezone).endOf('day'),
    ],
    'Last Month': [
      timezone === 'UTC' ? moment.utc().subtract(1, 'month').startOf('month').startOf('day') : moment.tz(timezone).subtract(1, 'month').startOf('month').startOf('day'),
      timezone === 'UTC' ? moment.utc().subtract(1, 'month').endOf('month').endOf('day') : moment.tz(timezone).subtract(1, 'month').endOf('month').endOf('day'),
    ],
    'Last 30 Days': [
      timezone === 'UTC' ? moment.utc().subtract(30, 'days').startOf('day') : moment.tz(timezone).subtract(30, 'days').startOf('day'),
      timezone === 'UTC' ? moment.utc().endOf('day') : moment.tz(timezone).endOf('day'),
    ],
    'This Quarter': [
      getThisQuarter.clone().startOf('day'),
      timezone === 'UTC' ? moment.utc().endOf('day') : moment.tz(timezone).endOf('day'),
    ],
    'Last Quarter': [
      getPreviousQuarter.clone().startOf('day'),
      getPreviousQuarter.clone().endOf('quarter').endOf('day'),
    ],
    'All Time': [
      timezone === 'UTC' ? moment.utc('2020-01-01').startOf('day') : moment.tz('2020-01-01', timezone).startOf('day'),
      timezone === 'UTC' ? moment.utc().endOf('day') : moment.tz(timezone).endOf('day'),
    ],
  };
};

// Default ranges for backward compatibility (uses UTC)
export const ranges = getTimezoneAwareRanges('UTC');

function filterDataByDate(data, startDate, endDate, timezone = 'UTC') {
  // for all time filter
  if (startDate <= moment('2020-01-01').unix()) {
    return data;
  }

  const x = data.filter((obj) => {
    let date = obj.xAxis;
    if (typeof date === 'string') {
      date = timezone === 'UTC' ? moment.utc(date).unix() : moment.tz(date, timezone).unix();
    }
    if (date.toString().length < 10) {
      date = date * 1000;
    }
    return date >= startDate && date <= endDate;
  });
  return x;
}

/**
 * This hook is for byDate charts
 * Mainly applicable for objects with xAxis as date
 * @param {Array} data - Array of data objects with xAxis date property
 * @param {string} initialRangeKey - Initial range key (default: 'Last 7 Days')
 * @param {string} timezone - IANA timezone identifier (default: 'UTC')
 */
function useByDateFilter(data, initialRangeKey = 'Last 7 Days', timezone = 'UTC') {
  const timezoneRanges = getTimezoneAwareRanges(timezone);
  const [rangeKey, setRangeKey] = useState(initialRangeKey);
  const [startDateEndDate, setStartDateEndDate] = useState(timezoneRanges['Last 7 Days']);
  const [filteredData, setFilteredData] = useState(data);
  const [customDateRange, setCustomDateRange] = useState([]);

  useEffect(() => {
    const currentRanges = getTimezoneAwareRanges(timezone);
    if (rangeKey && Object.keys(currentRanges).indexOf(rangeKey) >= 0) {
      const getSelectedStartDate = currentRanges[rangeKey][0].unix();
      const getSelectedEndDate = currentRanges[rangeKey][1].unix();
      setFilteredData(filterDataByDate(data, getSelectedStartDate, getSelectedEndDate, timezone));
    }
  }, [data, rangeKey, timezone]);

  useEffect(() => {
    if (customDateRange && customDateRange.length === 2) {
      try {
        const getSelectedStartDate = timezone === 'UTC'
          ? moment.utc(customDateRange[0]).unix()
          : moment.tz(customDateRange[0], timezone).unix();
        const getSelectedEndDate = timezone === 'UTC'
          ? moment.utc(customDateRange[1]).unix()
          : moment.tz(customDateRange[1], timezone).unix();
        setFilteredData(filterDataByDate(data, getSelectedStartDate, getSelectedEndDate, timezone));
        setRangeKey('Custom');
      } catch (e) {
        console.log(e);
      }
    }
  }, [data, customDateRange, timezone]);

  useEffect(() => {
    if (data && data.length > 0) {
      const getDate = data.map((a) => ({
        timestamp: a.date,
        date: a.xAxis,
      }));

      // sort them by desc dates
      getDate.sort(function (a, b) {
        return a.timestamp - b.timestamp;
      });

      // get startdate and end date
      const startDate = getDate[0].date;
      const endDate = getDate.slice(-1)[0].date;

      // combined start date & end date with timezone awareness
      const startEndDate = timezone === 'UTC'
        ? [moment.utc(startDate), moment.utc(endDate)]
        : [moment.tz(startDate, timezone), moment.tz(endDate, timezone)];
      setStartDateEndDate(startEndDate);
      setFilteredData(filterDataByDate(data, startEndDate[0].unix(), startEndDate[1].unix(), timezone));
      setRangeKey('Custom');
    }
  }, [data, timezone]);

  if (
    startDateEndDate.length &&
    (startDateEndDate[0].year() === 1970 || startDateEndDate[1].year() === 1970)
  ) {
    const currentRanges = getTimezoneAwareRanges(timezone);
    setStartDateEndDate(currentRanges['Last 7 Days']);
  }

  return {
    rangeKey,
    setRangeKey,
    filteredData,
    customDateRange,
    setCustomDateRange,
    setStartDateEndDate,
    startDateEndDate,
    timezone,
  };
}

export { useByDateFilter };
